/**
 * Sanitiseringshjelper for prosjektkommentarer (HIGH-8).
 *
 * Ingen React- eller Supabase-avhengigheter — kan importeres trygt i unit-tester
 * uten at Supabase-klienten initialiseres.
 */

import type { UserLookup } from "@/hooks/use-users";

/** Escape regex-spesialtegn så vi kan matche brukernavn trygt. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * HTML-escaper en ren tekststreng slik at den er trygg å sette inn via
 * innerHTML. Bare de fem tegnene som er farlige i HTML-kontekst escapes.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function mentionSpan(escapedName: string, isMe: boolean): string {
  const className = isMe
    ? "underline font-semibold"
    : "text-primary font-medium";
  // escapedName er allerede HTML-escaped av kallende kode.
  return `<span class="${className}">${escapedName}</span>`;
}

/**
 * Konverterer @-mentions i en plain-text body til HTML-spans.
 *
 * Sikkerhetsgaranti: body-teksten HTML-escapes FØR spans injiseres, slik at
 * angriper-payload som `<img src=x onerror=alert(1)>` rendres som synlig tekst
 * og ikke som HTML-elementer (HIGH-8).
 */
export function renderMentions(
  body: string,
  mentionedIds: string[],
  users: UserLookup[],
  isMe: boolean,
): string {
  // Escape body-teksten FØR vi injiserer HTML-spans.
  const escapedBody = escapeHtml(body);

  // Eldre kommentarer lagret @[Navn](id)-markdown. Strip dem først.
  // name er hentet fra allerede HTML-escaped body — ikke escape igjen.
  let out = escapedBody.replace(
    /@\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, name) => mentionSpan(String(name), isMe),
  );

  // Nye kommentarer lagrer plain "@FullName" + IDs separat.
  for (const id of mentionedIds) {
    const u = users.find((x) => x.id === id);
    if (!u) continue;
    const escapedName = escapeHtml(u.full_name);
    out = out.replace(
      new RegExp(`@${escapeRegex(escapedName)}\\b`, "g"),
      mentionSpan(escapedName, isMe),
    );
  }

  // Linjeskift må bli <br> — output rendres via innerHTML, der rå \n
  // kollapser til mellomrom (VIB-1696). Etter escaping er dette trygt.
  return out.replace(/\r\n|\r|\n/g, "<br>");
}

/** Tillatte tagger og attributter når Monday-HTML i kommentarer rendres (DOMPurify). */
export const HTML_SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "a", "br", "p", "div", "span", "strong", "em", "b", "i", "u",
    "ul", "ol", "li", "blockquote", "code", "pre", "hr",
    "h1", "h2", "h3", "h4", "h5", "h6", "img", "table", "thead", "tbody", "tr", "th", "td",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "title"],
};
